/*
 * src/store/userStore.ts
 *
 * This file defines the Zustand store for managing user-related state.
 * It is responsible for:
 * - Storing the current authenticated user's profile and session information.
 * - Managing lists of users and teams accessible to the current user.
 * - Handling user authentication operations: login, logout, password changes, password reset.
 * - Managing CRUD operations for users and teams (if authorized).
 * - Storing the currently selected team and user language preferences.
 * - Initializing the application state by fetching necessary user and team data.
 * - Reporting authentication and data loading errors.
 */
import { create } from 'zustand';
import { PASSWORD_ATTEMPTS_KEY } from '../lib/loginCooldown';
import { parseLastMatchDefaults } from '../utils/teamMatchSettings';
import { User, Team, Player, PlayerStatistics } from '../types';
import { normalizeUserRole } from '../utils/userRole';
import {
  clearAllStoredAuthState,
  clearStoredAuthSession,
  clearStoredAuthUser,
  getCurrentAuthSession,
  getStoredAccessToken,
  getStoredAuthSession,
  loginWithPassword,
  logoutAuthSession,
  changePasswordWithCurrentPassword,
  refreshAuthSession,
  isInvalidAuthSessionError,
  saveStoredAuthUser,
  saveAuthSession,
  verifyOtpLogin,
} from '../lib/authApi';
import { createTeamApi, deleteTeamApi, listTeamsApi, updateTeamApi } from '../lib/teamsApi';
import { createUserApi, getMyProfileApi, listProfilesApi, ProfilesApiError, updateMyProfileApi, updateProfileApi } from '../lib/profilesApi';
import { invalidateCachedValue } from '../lib/requestCache';
import { useMatchStore } from './matchStore';
import { useTeamStore } from './teamStore';
import { useClubDetailStore } from './clubDetailStore';
import { useIssueFormStore } from './issueFormStore';
import { useHighPriorityIssueStore } from './highPriorityIssueStore';
import { useLivePresenceStore } from './livePresenceStore';
import { normalizePositionPreferences } from '../utils/player';
import { trackEvent } from '../utils/analytics';
import { decryptIfEncrypted } from '../utils/encryption';
import { clearStoragePreservingPwaInstallPreference } from '../utils/pwaInstallPreference';
import { protectedAdminFetch, throwIfProtectedAdminResponseNotOk } from '../lib/protectedAdminApi';
import { getTeamPlayerReferenceIds } from '../utils/teamPlayerReferences';

let initializePromise: Promise<void> | null = null;
let authStateVersion = 0;

type ProfileRecord = Record<string, unknown>;
type TeamRecord = Record<string, unknown>;
type TeamUpdatePayload = Omit<Partial<Team>, 'players' | 'standins' | 'constraints'> & {
  players?: Array<Player | string>;
  standins?: Array<string | { id: string | number }>;
  constraints?: Record<string, unknown> | null;
};

const parseConstraints = (value: unknown): Record<string, unknown> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return value as Record<string, unknown>;
};


/**
 * Definieert de vorm van de gebruikers-state die door Zustand wordt beheerd.
 */
interface UserState {
  /** Het momenteel geauthenticeerde gebruikersobject, of null als er geen gebruiker is ingelogd. */
  currentUser: User | null;
  /** Array van alle gebruikers in het systeem (voornamelijk voor admin-weergaven). */
  users: User[];
  /** Array van teams, mogelijk gefilterd op de club of rollen van de huidige gebruiker. */
  teams: Team[];
  /** Vlag die aangeeft of de store en initiële gebruikersgegevens zijn geïnitialiseerd. */
  isInitialized: boolean;
  /** Vlag die aangeeft of een authenticatie- of gegevenslaadbewerking wordt uitgevoerd. */
  isLoading: boolean;
  /** Het ID van het momenteel door de gebruiker geselecteerde team. */
  currentTeamId: string | null;
  /** Slaat eventuele foutmeldingen op met betrekking tot verbindings- of authenticatieproblemen. */
  connectionError: string | null;

  /**
   * Logt een gebruiker in met het opgegeven e-mailadres en wachtwoord.
   * @param email - Het e-mailadres van de gebruiker.
   * @param password - Het wachtwoord van de gebruiker.
   * @returns Een promise die resulteert in true bij succesvolle login, anders false.
   */
  login: (email: string, password: string) => Promise<{ success: boolean; code?: string; message?: string; retryAfterSeconds?: number }>;
  /** Completes login with a one-time e-mail code or magic-link token. */
  loginWithMagicCode: (email: string, code: string) => Promise<{ success: boolean; code?: string; message?: string; retryAfterSeconds?: number }>;
  /** Logt de huidige gebruiker uit en wist de gerelateerde state. */
  logout: () => Promise<void>;
  /**
   * Wijzigt het wachtwoord van de huidige gebruiker.
   * @param currentPassword - Het huidige wachtwoord van de gebruiker.
   * @param newPassword - Het gewenste nieuwe wachtwoord.
   * @returns Een promise die resulteert in true bij succes, of een fout genereert.
   */
  changePassword: (currentPassword: string, newPassword: string) => Promise<boolean>;
  /**
   * Start een verzoek voor het opnieuw instellen van het wachtwoord voor het opgegeven e-mailadres.
   * @param email - Het e-mailadres waarnaar de resetlink moet worden verzonden.
   * @returns Een promise die resulteert in true bij succes, of een fout genereert.
   */
  resetPassword: (email: string) => Promise<boolean>;
  /** Laadt de lijst met alle gebruikers (meestal voor admin-gebruikers). */
  loadUsers: () => Promise<void>;
  /** Laadt de lijst met teams die toegankelijk zijn voor de huidige gebruiker. */
  loadTeams: () => Promise<void>;
  /**
   * Creëert een nieuw team.
   * @param team - Teamgegevens exclusief ID en audit-tijdstempels.
   * @returns Een promise die resulteert in het ID van het nieuw aangemaakte team.
   */
  createTeam: (team: Omit<Team, 'id' | 'createdAt' | 'updatedAt' | 'created_at' | 'updated_at'>) => Promise<string>;
  /**
   * Werkt een bestaand team bij.
   * @param id - Het ID van het bij te werken team.
   * @param updates - Gedeeltelijke teamgegevens om bij te werken.
   */
  updateTeam: (id: string, updates: Omit<Partial<Team>, 'players' | 'standins' | 'constraints'> & {
    players?: Array<Player | string>;
    standins?: Array<string | { id: string | number }>;
    constraints?: Record<string, unknown> | null;
  }) => Promise<void>;
  /**
   * Verwijdert een team.
   * @param id - Het ID van het te verwijderen team.
   */
  deleteTeam: (id: string) => Promise<void>;
  /**
   * Voegt een nieuwe gebruiker toe aan het systeem.
   * @param user - Gebruikersgegevens inclusief e-mail, initieel wachtwoord, weergavenaam, rol, taal en optioneel club-ID.
   */
  addUser: (user: { email: string; displayName: string; role: 'admin' | 'club-manager' | 'user'; language: string; club_id?: string }) => Promise<void>;
  /**
   * Werkt het profiel van een bestaande gebruiker bij.
   * @param id - Het ID van de bij te werken gebruiker.
   * @param updates - Gedeeltelijke gebruikersgegevens om bij te werken.
   */
  updateUser: (id: string, updates: Partial<User>) => Promise<void>;
  /**
   * Verwijdert een gebruiker uit het systeem.
   * @param id - Het ID van de te verwijderen gebruiker.
   */
  deleteUser: (id: string) => Promise<void>;
  /**
   * Stelt de voorkeurstaal voor de huidige gebruiker in.
   * @param lang - De taalcode (bijv. 'en', 'nl').
   */
  setLanguage: (lang: string) => Promise<void>;
  /** Controleert of de huidige gebruiker een 'admin'-rol heeft. */
  isAdmin: () => boolean;
  /** Controleert of de huidige gebruiker een 'club-manager'-rol heeft. */
  isClubManager: () => boolean;
  /** Initialiseert de user store, haalt sessiegegevens en initiële lijsten op. */
  initialize: () => Promise<void>;
  /** Ruimt lokale auth/session state op zonder rehydratie toe te laten. */
  clearAuthState: () => void;
  /**
   * Stelt het momenteel actieve team voor de gebruiker in.
   * @param id - Het ID van het team om als huidig in te stellen, of null om te wissen.
   */
  setCurrentTeam: (id: string | null) => void;
  /** Haalt het volledige object op voor het momenteel geselecteerde team. */
  getCurrentTeam: () => Team | null;
}

const INIT_TIMEOUT_MS = 10000;

const withTimeout = async <T>(promise: PromiseLike<T>, label: string): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(`${label} timed out`)), INIT_TIMEOUT_MS);
  });
  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  }
};

const asProfileRecord = (value: unknown): ProfileRecord =>
  (value && typeof value === 'object' ? value : {}) as ProfileRecord;

const asTeamRecord = (value: unknown): TeamRecord =>
  (value && typeof value === 'object' ? value : {}) as TeamRecord;

const mapProfileToUser = (profile: ProfileRecord): User => ({
  id: String(profile.id),
  email: typeof profile.email === 'string' ? profile.email : '',
  username: typeof profile.email === 'string' ? profile.email : '',
  displayName:
    typeof profile.display_name === 'string'
      ? profile.display_name
      : (typeof profile.email === 'string' ? profile.email : ''),
  iscoach: Boolean(profile.iscoach),
  istrainer: Boolean(profile.istrainer),
  language: typeof profile.language === 'string' ? profile.language : 'en',
  role: normalizeUserRole(profile.role),
  club_id: typeof profile.club_id === 'string' ? profile.club_id : undefined,
  status: typeof profile.status === 'string' ? profile.status : undefined,
  teamIds: [],
  createdAt: typeof profile.created_at === 'string' ? profile.created_at : '',
  updatedAt: typeof profile.updated_at === 'string' ? profile.updated_at : '',
  created_at: typeof profile.created_at === 'string' ? profile.created_at : '',
  updated_at: typeof profile.updated_at === 'string' ? profile.updated_at : '',
  email_delivery_health:
    profile.email_delivery_health && typeof profile.email_delivery_health === 'object'
      ? profile.email_delivery_health as User['email_delivery_health']
      : null,
  email_delivery_mode: profile.email_delivery_mode === 'compatibility_7bit' ? 'compatibility_7bit' : 'standard',
  nonessential_email_unsubscribed: profile.nonessential_email_unsubscribed === true,
  local_auth_migrated: profile.local_auth_migrated === true,
});

const persistCurrentUser = (user: User | null) => {
  saveStoredAuthUser(
    user
      ? {
          id: user.id,
          email: user.email,
          role: user.role,
          club_id: user.club_id,
        }
      : null
  );
};

const clearLegacyAuthStorage = (): void => {
  if (typeof window === 'undefined') return;
  const removeFromStorage = (storage: Storage) => {
    const keysToRemove: string[] = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (!key) continue;
      const isLegacyAuthTokenKey = key.includes('.auth.token') || (key.startsWith('sb-') && key.includes('-auth-token'));
      if (isLegacyAuthTokenKey) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => storage.removeItem(key));
  };

  try {
    removeFromStorage(window.localStorage);
  } catch {
    // Ignore storage access issues
  }

  try {
    removeFromStorage(window.sessionStorage);
  } catch {
    // Ignore storage access issues
  }
};

const clearBrowserPersistence = (): void => {
  if (typeof window === 'undefined') return;

  try {
    clearStoragePreservingPwaInstallPreference(window.localStorage);
  } catch {
    // Ignore storage access issues
  }

  try {
    const passwordAttempts = window.sessionStorage.getItem(PASSWORD_ATTEMPTS_KEY);
    window.sessionStorage.clear();
    if (passwordAttempts) window.sessionStorage.setItem(PASSWORD_ATTEMPTS_KEY, passwordAttempts);
  } catch {
    // Ignore storage access issues
  }

  if (typeof globalThis !== 'undefined' && 'caches' in globalThis) {
    void (async () => {
      try {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames
          .filter((cacheName) => !cacheName.startsWith('dohockey-portal-'))
          .map((cacheName) => caches.delete(cacheName)));
      } catch {
        // Ignore Cache Storage issues
      }
    })();
  }
};

/**
 * De Zustand-store voor het beheren van de gebruikers-state.
 *
 * @param set - Functie om de state bij te werken.
 * @param get - Functie om de huidige state op te halen.
 * @returns De user store met state en acties.
 */
export const useUserStore = create<UserState>((set, get) => ({
  currentUser: null,
  users: [],
  teams: [],
  isInitialized: false,
  isLoading: false,
  currentTeamId: null,
  connectionError: null,
  clearAuthState: () => {
    authStateVersion += 1;
    initializePromise = null;
    clearAllStoredAuthState();
    clearLegacyAuthStorage();
    clearBrowserPersistence();
    useTeamStore.getState().reset();
    useMatchStore.getState().reset();
    useClubDetailStore.getState().reset();
    useIssueFormStore.getState().close();
    useHighPriorityIssueStore.getState().close();
    useHighPriorityIssueStore.getState().setIssues([]);
    useLivePresenceStore.getState().resetPortalPresence();
    set({
      currentUser: null,
      teams: [],
      users: [],
      isInitialized: true,
      isLoading: false,
      currentTeamId: null,
      connectionError: null
    });
  },

  initialize: async () => {
    if (get().isInitialized) return;
    if (initializePromise) {
      await initializePromise;
      return;
    }

    const initVersion = authStateVersion;
    initializePromise = (async () => {
    try {
      set({ isLoading: true, connectionError: null });

      const storedSession = getStoredAuthSession();
      if (!storedSession || (!storedSession.session.cookie_authenticated && !storedSession.session.access_token)) {
        clearStoredAuthSession();
        clearStoredAuthUser();
        clearLegacyAuthStorage();
        set({
          currentUser: null,
          teams: [],
          users: [],
          currentTeamId: null
        });
        return;
      }

      let authState = storedSession;
      try {
        let migratedLegacySession = false;
        if (storedSession.session.refresh_token) {
          await withTimeout(
            Promise.resolve(refreshAuthSession(storedSession.session.refresh_token)),
            'Legacy session migration'
          );
          migratedLegacySession = true;
        }
        const validatedSession = await withTimeout(
          Promise.resolve(getCurrentAuthSession(migratedLegacySession ? undefined : storedSession.session.access_token)),
          'Session validation'
        );
        if (initVersion !== authStateVersion) return;
        authState = {
          session: {
            cookie_authenticated: true,
            token_type: validatedSession.session.token_type || storedSession.session.token_type,
            expires_in: validatedSession.session.expires_in ?? storedSession.session.expires_in,
            expires_at: validatedSession.session.expires_at ?? storedSession.session.expires_at,
          },
          user: validatedSession.user || storedSession.user,
        };
        saveAuthSession(authState);
      } catch (error) {
        if (!isInvalidAuthSessionError(error)) throw error;
        const refreshedSession = await withTimeout(
          Promise.resolve(refreshAuthSession(storedSession.session.refresh_token)),
          'Session refresh'
        );
        if (initVersion !== authStateVersion) return;
        authState = {
          session: {
            cookie_authenticated: true,
            token_type: refreshedSession.session.token_type || storedSession.session.token_type,
            expires_in: refreshedSession.session.expires_in ?? storedSession.session.expires_in,
            expires_at: refreshedSession.session.expires_at ?? storedSession.session.expires_at,
          },
          user: refreshedSession.user || storedSession.user,
        };
        saveAuthSession(authState);
      }

      const authUser = (authState.user as { id?: string } | null) ?? null;
      const authUserId = authUser?.id ?? null;
      if (authUserId) {
        const userProfile = await withTimeout(
          Promise.resolve(getMyProfileApi()),
          'Profile load'
        );
        if (initVersion !== authStateVersion) return;

        if (!userProfile) {
          set({
            isInitialized: true,
            currentUser: null,
            connectionError: 'Profile error: empty profile response'
          });
          return;
        }

        const mappedUser: User = mapProfileToUser(asProfileRecord(userProfile));
        persistCurrentUser(mappedUser);
        set({ currentUser: mappedUser });

        const shouldLoadUsers = mappedUser.role === 'admin' || mappedUser.role === 'club-manager';
        const [usersError, teamsError] = await Promise.allSettled([
          shouldLoadUsers ? withTimeout(get().loadUsers(), 'Users load') : Promise.resolve(),
          withTimeout(get().loadTeams(), 'Teams load')
        ]).then(results => results.map(result =>
          result.status === 'rejected' ? result.reason : null
        ));

        if (usersError || teamsError) {
          const errors = [];
          if (usersError) errors.push('Failed to load users: ' + usersError.message);
          if (teamsError) errors.push('Failed to load teams: ' + teamsError.message);
          set({ connectionError: errors.join('. ') });
        }

        // Restore last selected team (if still available)
        try {
          if (typeof sessionStorage !== 'undefined') {
            const storedTeamId = sessionStorage.getItem('currentTeamId');
            if (storedTeamId) {
              const available = get().teams.find(team => team.id === storedTeamId);
              if (available) {
                set({ currentTeamId: storedTeamId });
              } else {
                sessionStorage.removeItem('currentTeamId');
              }
            }
          }
        } catch {
          // ignore storage failures
        }
      } else {
        clearStoredAuthSession();
        clearStoredAuthUser();
        clearLegacyAuthStorage();
        set({
          currentUser: null,
          teams: [],
          users: [],
          currentTeamId: null
        });
      }
    } catch (error) {
      if (initVersion !== authStateVersion) return;
      const unauthorized =
        isInvalidAuthSessionError(error) ||
        (error instanceof ProfilesApiError && error.status === 401);
      if (unauthorized) {
        clearStoredAuthSession();
        clearStoredAuthUser();
        clearLegacyAuthStorage();
        set({
          currentUser: null,
          teams: [],
          users: [],
          currentTeamId: null,
          connectionError: null,
          isInitialized: true,
        });
        return;
      }
      // Keep shared sign-in markers on transport/server failure; removing the
      // user key would log other tabs out through the storage event listener.
      set({
        connectionError: 'Initialization error: ' + (error instanceof Error ? error.message : 'Unknown error'),
        isInitialized: true
      });
    } finally {
      if (initVersion === authStateVersion) {
        set({ isLoading: false, isInitialized: true });
      }
    }
    })();

    try {
      await initializePromise;
    } finally {
      initializePromise = null;
    }
  },

  loadUsers: async () => {
    try {
      set({ connectionError: null });
      invalidateCachedValue('profiles:list:');
      const users: Awaited<ReturnType<typeof listProfilesApi>> = [];
      for (let offset = 0; ; offset += 500) {
        const page = await listProfilesApi({ limit: 500, offset });
        users.push(...page);
        if (page.length < 500) break;
      }

      const mappedUsers: User[] = users.map((user) => mapProfileToUser(asProfileRecord(user)));

      set({ users: mappedUsers, connectionError: null });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred while loading users';
      set({ connectionError: errorMessage });
      throw error;
    }
  },

  login: async (email, password) => {
    const trackFailure = (reason: string) => trackEvent('login_failed', { reason });

    try {
      get().clearAuthState();
      set({ isLoading: true, connectionError: null });

      const authPayload = await loginWithPassword(email, password);
      if (authPayload.ok === false) {
        trackFailure(authPayload.code || 'authentication_rejected');
        return { success: false, code: authPayload.code, message: authPayload.error, retryAfterSeconds: authPayload.retry_after_seconds };
      }
      saveAuthSession(authPayload);

      if (!authPayload?.session?.cookie_authenticated && !authPayload?.session?.access_token) {
        set({ connectionError: 'No active session returned from authentication' });
        trackFailure('missing_session');
        return { success: false };
      }

      const authUser = (authPayload.user as { id?: string } | null) ?? null;
      const authUserId = authUser?.id;
      if (!authUserId) {
        set({ connectionError: 'No user returned from authentication' });
        trackFailure('missing_user');
        return { success: false };
      }

      const profilePayload = await getMyProfileApi();
      if (!profilePayload) {
        set({ connectionError: 'Failed to fetch user profile: empty response' });
        trackFailure('profile_fetch_error');
        return { success: false };
      }

      const mappedUser: User = mapProfileToUser(asProfileRecord(profilePayload));

      persistCurrentUser(mappedUser);
      set({ currentUser: mappedUser, connectionError: null });

      const shouldLoadUsers = mappedUser.role === 'admin' || mappedUser.role === 'club-manager';
      await Promise.all([
        shouldLoadUsers
          ? get().loadUsers().catch(err => {
            set({ connectionError: 'Error loading users: ' + err.message });
          })
          : Promise.resolve(),
        get().loadTeams().catch(err => {
          set({ connectionError: 'Error loading teams: ' + err.message });
        })
      ]);

      set({ isInitialized: true });
      trackEvent('login_success');
      return { success: true };
    } catch (err) {
      set({ connectionError: 'Login error: ' + (err instanceof Error ? err.message : 'Unknown error') });
      trackFailure('exception');
      return { success: false };
    } finally {
      set({ isLoading: false, isInitialized: true });
    }
  },

  loginWithMagicCode: async (email, code) => {
    const trackFailure = (reason: string) => trackEvent('magic_login_failed', { reason });

    try {
      get().clearAuthState();
      set({ isLoading: true, connectionError: null });

      const authPayload = await verifyOtpLogin(email, code);
      if (authPayload.ok === false) {
        trackFailure(authPayload.code || 'authentication_rejected');
        return { success: false, code: authPayload.code, message: authPayload.error, retryAfterSeconds: authPayload.retry_after_seconds };
      }
      saveAuthSession(authPayload);

      if (!authPayload.session.cookie_authenticated && !authPayload.session.access_token) {
        set({ connectionError: 'No active session returned from authentication' });
        trackFailure('missing_session');
        return { success: false };
      }

      const authUser = (authPayload.user as { id?: string } | null) ?? null;
      if (!authUser?.id) {
        set({ connectionError: 'No user returned from authentication' });
        trackFailure('missing_user');
        return { success: false };
      }

      const profilePayload = await getMyProfileApi();
      if (!profilePayload) {
        set({ connectionError: 'Failed to fetch user profile: empty response' });
        trackFailure('profile_fetch_error');
        return { success: false };
      }

      const mappedUser = mapProfileToUser(asProfileRecord(profilePayload));
      persistCurrentUser(mappedUser);
      set({ currentUser: mappedUser, connectionError: null });

      const shouldLoadUsers = mappedUser.role === 'admin' || mappedUser.role === 'club-manager';
      await Promise.all([
        shouldLoadUsers
          ? get().loadUsers().catch(err => set({ connectionError: 'Error loading users: ' + err.message }))
          : Promise.resolve(),
        get().loadTeams().catch(err => set({ connectionError: 'Error loading teams: ' + err.message })),
      ]);

      set({ isInitialized: true });
      trackEvent('magic_login_success');
      return { success: true };
    } catch (err) {
      set({ connectionError: 'Magic login error: ' + (err instanceof Error ? err.message : 'Unknown error') });
      trackFailure('exception');
      return { success: false, message: err instanceof Error ? err.message : undefined };
    } finally {
      set({ isLoading: false, isInitialized: true });
    }
  },

  changePassword: async (currentPassword: string, newPassword: string) => {
    const accessToken = getStoredAccessToken();
    if (!getStoredAuthSession()) {
      throw new Error('Geen actieve sessie gevonden.');
    }

    await changePasswordWithCurrentPassword(currentPassword, newPassword, accessToken || undefined);
    return true;
  },

  resetPassword: async (email: string) => {
    void email;
    throw new Error('Password reset is not migrated yet. Use backend auth endpoint first.');
  },

  logout: async () => {
    const accessToken = getStoredAccessToken();
    try {
      set({ isLoading: true, connectionError: null });
      get().clearAuthState();
      try {
        await logoutAuthSession(accessToken || undefined);
      } catch {
        // Ignore backend logout failure; local logout already happened
      }
    } catch {
      get().clearAuthState();
    }
  },

  loadTeams: async () => {
    try {
      const teamsData = await listTeamsApi();
      const maybeDecrypt = async (value: string | undefined, clubId?: string | null) => {
        return await decryptIfEncrypted({ value, clubId });
      };

      // Process teams and their players
      const processedTeams = await Promise.all((Array.isArray(teamsData) ? teamsData : []).map(async (rawTeam) => {
        const team = asTeamRecord(rawTeam);
        const playerIds = getTeamPlayerReferenceIds(team.players);
        const playerCount = playerIds.length;
        // Process players array
        const players = (Array.isArray(team.players) ? team.players : []).map((p) => {
          // If player is already a full object, use it directly
          if (p && typeof p === 'object' && 'id' in (p as Record<string, unknown>)) {
            const playerRecord = p as Record<string, unknown>;
            const normalizedPositions = normalizePositionPreferences(
              playerRecord.positionPreferencesByVariant && typeof playerRecord.positionPreferencesByVariant === 'object'
                ? {
                    ...(playerRecord.positionPreferencesByVariant as Record<string, unknown>),
                    variant: playerRecord.positionPreferenceVariant
                  }
                : playerRecord.positions ?? playerRecord.positionPreferences
            );
            return {
              id: String(playerRecord.id),
              clubId: typeof playerRecord.club_id === 'string' ? playerRecord.club_id : '',
              name: typeof playerRecord.name === 'string' ? playerRecord.name : '',
              number: String(playerRecord.number ?? ''),
              positionPreferences: normalizedPositions.preferences,
              positionPreferenceVariant: normalizedPositions.variant,
              positionPreferencesByVariant: normalizedPositions.variants,
              hasPositionPriority: normalizedPositions.hasExplicitPriorities,
              gender: playerRecord.gender === 'male' || playerRecord.gender === 'female' ? playerRecord.gender : null,
              isGoalkeeper: Boolean(playerRecord.is_goalkeeper),
              condition: typeof playerRecord.condition === 'number' ? playerRecord.condition : undefined,
              performance: typeof playerRecord.performance === 'number' ? playerRecord.performance : undefined,
              playingTime: typeof playerRecord.playing_time === 'number' ? playerRecord.playing_time : undefined,
              goals: typeof playerRecord.goals === 'number' ? playerRecord.goals : undefined,
              evaluations: Array.isArray(playerRecord.evaluations) ? playerRecord.evaluations : [],
              statistics: {
                totalGoals: (playerRecord.statistics as PlayerStatistics | undefined)?.totalGoals ?? 0,
                totalPlayingTime: (playerRecord.statistics as PlayerStatistics | undefined)?.totalPlayingTime ?? 0,
                ...((playerRecord.statistics as Record<string, number>) || {}),
              },
              // stand-in flag removed from Player model; derive contextually when needed
            };
          }
          // If it's just an ID, return null (will be filtered out)
          return null;
        }).filter(Boolean); // Remove any null entries

        // Keep standins as array of IDs (strings)
        const standins = (Array.isArray(team.standins) ? team.standins : []) as string[];

        const rawName = typeof team.name === 'string' ? team.name : '';
        const rawKnhbTeamId =
          typeof team.knhb_teamid === 'string' && team.knhb_teamid.trim() ? team.knhb_teamid : undefined;
        const rawKnhbTeamIdIndoor =
          typeof team.knhb_teamid_indoor === 'string' && team.knhb_teamid_indoor.trim()
            ? team.knhb_teamid_indoor
            : undefined;

        const [name, knhbTeamId, knhbTeamIdIndoor] = await Promise.all([
          maybeDecrypt(rawName, typeof team.club_id === 'string' ? team.club_id : null),
          maybeDecrypt(rawKnhbTeamId, typeof team.club_id === 'string' ? team.club_id : null),
          maybeDecrypt(rawKnhbTeamIdIndoor, typeof team.club_id === 'string' ? team.club_id : null),
        ]);

        return {
          id: String(team.id),
          name: name || rawName,
          club_id: typeof team.club_id === 'string' ? team.club_id : undefined,
          seasonId: typeof team.season_id === 'string' ? team.season_id : undefined,
          continuationSeasonIds: Array.isArray(team.continuation_season_ids)
            ? team.continuation_season_ids.filter((id): id is string => typeof id === 'string') : [],
          knhbTeamId: knhbTeamId ?? undefined,
          knhbTeamIdIndoor: knhbTeamIdIndoor ?? undefined,
          defaultIndoorFormationKey: typeof team.default_indoor_formation_key === 'string' ? team.default_indoor_formation_key : null,
          defaultFormationKey: typeof team.default_formation_key === 'string' ? team.default_formation_key : undefined,
          gender: (team.gender === 'male' || team.gender === 'female' || team.gender === 'mixed') ? team.gender as Team['gender'] : null,
          playingEnvironment: (team.playing_environment === 'indoor' || team.playing_environment === 'both' ? team.playing_environment : 'outdoor') as Team['playingEnvironment'],
          lastMatchDefaults: parseLastMatchDefaults(team.last_match_defaults),
          userIds: Array.isArray(team.user_ids) ? (team.user_ids as string[]) : [],
          players: players.filter(Boolean) as Player[],
          playerIds,
          playerCount: playerCount,
          standins: standins,
          coach: Array.isArray(team.coach) ? team.coach : [],
          constraints: parseConstraints(team.player_constraints ?? team.constraints),
          createdAt: typeof team.created_at === 'string' ? team.created_at : '',
          updatedAt: typeof team.updated_at === 'string' ? team.updated_at : '',
          created_at: typeof team.created_at === 'string' ? team.created_at : '',
          updated_at: typeof team.updated_at === 'string' ? team.updated_at : ''
        };
      }));

      set({ teams: processedTeams });
    } catch {
      // Ignore load failures to prevent breaking store updates
    }
  },

  createTeam: async (teamData) => {
    try {
      const { currentUser } = get();
      if (!currentUser) throw new Error('Not authenticated');

      const now = new Date().toISOString();
      const created = await createTeamApi({
        name: teamData.name,
        club_id: teamData.club_id ?? null,
        knhb_teamid: teamData.knhbTeamId ?? null,
        knhb_teamid_indoor: teamData.knhbTeamIdIndoor ?? null,
        default_indoor_formation_key: teamData.defaultIndoorFormationKey !== undefined ? teamData.defaultIndoorFormationKey : (teamData.playingEnvironment === 'indoor' ? teamData.defaultFormationKey : null),
        default_formation_key: teamData.playingEnvironment === 'indoor' ? '4-3-3' : teamData.defaultFormationKey || '4-3-3',
        gender: teamData.gender ?? null,
        playing_environment: teamData.playingEnvironment ?? 'outdoor',
        last_match_defaults: teamData.lastMatchDefaults ?? null,
        user_ids: teamData.userIds ?? [],
        players: teamData.players ?? [],
        standins: teamData.standins ?? [],
        coach: teamData.coach ?? [],
        player_constraints: teamData.constraints ?? {},
        created_at: now,
        updated_at: now
      } as Record<string, unknown>);

      await get().loadTeams();
      const createdId =
        created && typeof created === 'object' && 'id' in (created as Record<string, unknown>)
          ? (created as Record<string, unknown>).id
          : null;
      if (typeof createdId !== 'string' || !createdId) {
        throw new Error('Created team response did not include an id');
      }
      return createdId;
    } catch (error) {
      throw error;
    }
  },

  updateTeam: async (id, updates: TeamUpdatePayload) => {
    try {
      const updateData: Record<string, unknown> = {
        updated_at: new Date().toISOString()
      };

      if (updates.name) updateData.name = updates.name;
      if (updates.club_id !== undefined) updateData.club_id = updates.club_id;
      if ('knhbTeamId' in updates) {
        updateData.knhb_teamid = updates.knhbTeamId || null;
      }
      if ('knhbTeamIdIndoor' in updates) {
        updateData.knhb_teamid_indoor = updates.knhbTeamIdIndoor || null;
      }
      if (updates.gender !== undefined) updateData.gender = updates.gender;
      if (updates.playingEnvironment !== undefined) updateData.playing_environment = updates.playingEnvironment;
      if (updates.defaultIndoorFormationKey !== undefined) updateData.default_indoor_formation_key = updates.defaultIndoorFormationKey;
      if (updates.defaultFormationKey !== undefined) updateData.default_formation_key = updates.defaultFormationKey;
      if (updates.lastMatchDefaults !== undefined) updateData.last_match_defaults = updates.lastMatchDefaults ?? null;
      if (updates.userIds) updateData.user_ids = updates.userIds;
      if (updates.players) updateData.players = updates.players;
      if (updates.coach) updateData.coach = updates.coach;
      if (updates.constraints !== undefined) updateData.player_constraints = updates.constraints ?? {};
      if (updates.standins !== undefined) {
        const raw = updates.standins;
        updateData.standins = (raw || [])
          .map((p) => (p && typeof p === 'object' && 'id' in p ? String(p.id) : String(p)))
          .filter((id: string) => !!id && id !== 'null');
      }
      await updateTeamApi(id, updateData as Record<string, unknown>);

      await get().loadTeams();
    } catch (error) {
      throw error;
    }
  },

  deleteTeam: async (id) => {
    try {
      await deleteTeamApi(id);

      await get().loadTeams();
    } catch (error) {
      throw error;
    }
  },

  addUser: async (userData) => {
    try {
      await createUserApi(userData);
      await get().loadUsers();
    } catch (error) {
      throw error;
    }
  },

  updateUser: async (id, updates) => {
    try {
      await updateProfileApi(id, {
        ...(updates.email && { email: updates.email }),
        ...(updates.displayName && { display_name: updates.displayName }),
        ...(updates.role && { role: updates.role }),
        ...(updates.language && { language: updates.language }),
        ...(updates.club_id !== undefined && { club_id: updates.club_id })
      });

      await get().loadUsers();
    } catch (error) {
      throw error;
    }
  },

  deleteUser: async (id) => {

    try {
      const response = await protectedAdminFetch(`/api/v1/admin/users/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      });
      await throwIfProtectedAdminResponseNotOk(response, 'Kon gebruiker niet verwijderen via backend.');

      set((state) => ({
        users: state.users.filter((user) => user.id !== id),
      }));
      await get().loadUsers();

    } catch (error) {
      throw error;
    }
  },

  setLanguage: async (lang) => {
    const { currentUser } = get();
    if (!currentUser) return;
    const updatedUser = { ...currentUser, language: lang };
    persistCurrentUser(updatedUser);
    set({ currentUser: updatedUser });
    try {
      await updateMyProfileApi({ language: lang });
      await get().loadUsers();
    } catch {
      // Ignore language update failures; UI language already updated locally
    }
  },

  isAdmin: () => {
    const { currentUser } = get();
    return currentUser?.role === 'admin';
  },

  isClubManager: () => {
    const { currentUser } = get();
    return currentUser?.role === 'club-manager';
  },

  setCurrentTeam: (id) => {
    if (id && get().currentTeamId === id) return;
    set({ currentTeamId: id });
    // Reset related stores when switching or clearing teams to avoid stale state
    useTeamStore.getState().reset();
    useMatchStore.getState().reset();

    // Clear any team-specific local storage when no team is selected
    if (typeof sessionStorage !== 'undefined') {
      if (!id) {
        localStorage.removeItem('initialLineupForSubstitution');
        sessionStorage.removeItem('currentTeamId');
        sessionStorage.removeItem('currentMatchId');
        // Remove legacy global selection keys without restoring them into new tabs.
        localStorage.removeItem('currentTeamId');
        localStorage.removeItem('currentMatchId');
      } else {
        sessionStorage.setItem('currentTeamId', id);
        // Clear match selection when switching teams
        sessionStorage.removeItem('currentMatchId');
      }
    }
  },

  getCurrentTeam: () => {
    const { teams, currentTeamId } = get();
    return teams.find(team => team.id === currentTeamId) || null;
  }
}));
